// Floating red SOS button. Distinct from FeedbackButton because SOS is
// for LIVE production breakage — dialer dead, texts not delivering,
// dispositions not saving — not for typos or feature ideas. Positioned
// bottom-left by default so it doesn't collide with the feedback button
// (bottom-right). Same drag-to-any-corner UX so agents can stash it
// wherever it doesn't cover the screen they're working on.
//
// Click \u2192 SOSModal. Modal is two questions:
//   1. What broke? (dialer / sms / dispositions / login / ui / other)
//   2. One-line detail (optional)
// Everything else — page URL, active call, PD session, last 20 console
// errors — attaches automatically so the agent doesn't have to think.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import SOSModal from './SOSModal.jsx';

const STORAGE_KEY = 'sos-widget-anchor-v1';
const DEFAULT_ANCHOR = 'bottom-left';
const ANCHORS = ['bottom-left', 'bottom-right', 'top-left', 'top-right'];
const MARGIN = 20;
const DRAG_THRESHOLD = 8;

function getViewport() { return { width: window.innerWidth, height: window.innerHeight }; }

// Emit CSS edge props for the current anchor. Prefer this over pixel math
// whenever we're not actively dragging — the browser does the arithmetic
// against the CURRENT viewport on every paint, so scroll / resize / URL-bar
// toolbar-collapse don't cause the button to jitter.
function anchorToEdges(anchor) {
  switch (anchor) {
    case 'top-left':     return { top: `${MARGIN}px`,    left: `${MARGIN}px`,   right: 'auto',           bottom: 'auto' };
    case 'top-right':    return { top: `${MARGIN}px`,    right: `${MARGIN}px`,  left: 'auto',            bottom: 'auto' };
    case 'bottom-right': return { bottom: `${MARGIN}px`, right: `${MARGIN}px`,  left: 'auto',            top: 'auto'    };
    case 'bottom-left':
    default:             return { bottom: `${MARGIN}px`, left: `${MARGIN}px`,   right: 'auto',           top: 'auto'    };
  }
}

function clampPoint(p, vp, box) {
  const w = box?.width || 56;
  const h = box?.height || 56;
  return {
    x: Math.max(0, Math.min(p.x, vp.width - w)),
    y: Math.max(0, Math.min(p.y, vp.height - h)),
  };
}

function nearestAnchor(p, vp, box) {
  const w = box?.width || 56;
  const h = box?.height || 56;
  const cx = p.x + w / 2;
  const cy = p.y + h / 2;
  const left = cx < vp.width / 2;
  const top = cy < vp.height / 2;
  return `${top ? 'top' : 'bottom'}-${left ? 'left' : 'right'}`;
}

function loadAnchor() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return ANCHORS.includes(v) ? v : DEFAULT_ANCHOR;
  } catch { return DEFAULT_ANCHOR; }
}
function saveAnchor(a) { try { localStorage.setItem(STORAGE_KEY, a); } catch { /* ignore */ } }

export default function SOSButton() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(DEFAULT_ANCHOR);
  const [dragging, setDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState(null);
  const btnRef = useRef(null);
  const drag = useRef({ active: false, moved: false, startX: 0, startY: 0, grabX: 0, grabY: 0, box: null });

  useEffect(() => { setAnchor(loadAnchor()); }, []);

  const onPointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    drag.current = {
      active: true, moved: false,
      startX: e.clientX, startY: e.clientY,
      grabX: e.clientX - rect.left, grabY: e.clientY - rect.top,
      box: { width: rect.width, height: rect.height },
    };
    try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    if (!dragging) setDragging(true);
    setDragPoint(clampPoint({ x: e.clientX - d.grabX, y: e.clientY - d.grabY }, getViewport(), d.box));
  }, [dragging]);

  const endDrag = useCallback((e) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    const el = btnRef.current;
    try { el && el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (d.moved && dragPoint) {
      const snapped = nearestAnchor(dragPoint, getViewport(), d.box);
      setAnchor(snapped);
      saveAnchor(snapped);
    }
    setDragging(false);
    setDragPoint(null);
  }, [dragPoint]);

  const onClick = useCallback(() => {
    if (drag.current.moved) { drag.current.moved = false; return; }
    setOpen(true);
  }, []);

  // Only convert to explicit pixel coords while actively dragging. When
  // anchored, drive position via CSS edges (left/top/right/bottom) so the
  // browser handles the calc — the previous code recomputed a pixel `top`
  // on every render, and every subsequent Query/socket-triggered rerender
  // would re-read window.innerHeight and re-apply the 200ms CSS transition,
  // causing a visible bob during scroll (especially on macOS Chrome where
  // scrollbar-appear changes innerHeight, and iOS Safari where the URL bar
  // does the same). See audit 2026-08-26.
  const positionStyle = dragPoint
    ? { left: `${dragPoint.x}px`, top: `${dragPoint.y}px`, right: 'auto', bottom: 'auto' }
    : anchorToEdges(anchor);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="SOS: system is broken, I can't work"
        data-testid="sos-button"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={onClick}
        className="fixed z-40 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-full w-14 h-14 shadow-lg flex items-center justify-center select-none"
        style={{
          ...positionStyle,
          touchAction: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
          opacity: dragging ? 0.85 : 1,
          transition: dragging ? 'none' : 'left 200ms ease, top 200ms ease',
          boxShadow: dragging
            ? '0 16px 40px rgba(220,38,38,0.65)'
            : '0 8px 24px rgba(220,38,38,0.45)',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        </svg>
      </button>
      {open ? <SOSModal onClose={() => setOpen(false)} /> : null}
    </>
  );
}
